import { Stack, Typography, Box, Button } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SaveOutlined } from '@mui/icons-material';
import { ChapterTitle } from './styles';
import { formatTime, parseTime } from '@/helpers/formatTime';
import { FC, useEffect, useRef, useState } from 'react';
import { DocumentChapter } from '@/types/note';

interface DocumentChaptersProps {
  chapters: DocumentChapter[];
  handleChapterClick: (time: number) => void;
  isProcessing: boolean;
  isOwner?: boolean;
  onChaptersUpdate?: (chapters: DocumentChapter[]) => Promise<void>;
}

const DocumentChapters: FC<DocumentChaptersProps> = ({
  chapters,
  handleChapterClick,
  isProcessing,
  isOwner = false,
  onChaptersUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editableChapters, setEditableChapters] = useState<DocumentChapter[]>(chapters);
  const titleRefs = useRef<(HTMLElement | null)[]>([]);
  const summaryRefs = useRef<(HTMLElement | null)[]>([]);
  const showEditButton = isOwner && !isProcessing && chapters.length > 0;

  useEffect(() => {
    if (!isEditing) {
      setEditableChapters(chapters);
    }
  }, [chapters, isEditing]);

  const getEditedChapters = (): DocumentChapter[] =>
    editableChapters.map((chapter, index) => {
      const editedText = titleRefs.current[index]?.textContent;
      const editedSummary = summaryRefs.current[index]?.textContent;
      const textChanged = editedText != null && editedText !== chapter.text;
      const summaryChanged = editedSummary != null && editedSummary !== (chapter.summary ?? '');
      if (!textChanged && !summaryChanged) return chapter;
      return {
        ...chapter,
        ...(textChanged && { text: editedText }),
        ...(summaryChanged && { summary: editedSummary || null }),
      };
    });

  const handleEditOrSave = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    const updated = getEditedChapters();
    setEditableChapters(updated);
    setIsSaving(true);
    try {
      await onChaptersUpdate?.(updated);
      setIsEditing(false);
    } catch {
      // Error already reported and alerted by the save handler — stay in edit mode to allow retry
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditableChapters(chapters);
    setIsEditing(false);
  };

  const renderEmptyChapters = () => (
    <Typography variant="body1" fontWeight="400" pl={2}>
      {isProcessing ? 'Processing chapter markers...' : 'No chapters found'}
    </Typography>
  );

  return (
    <Box sx={{ maxWidth: '800px', width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={4} mb={1}>
        <Typography
          variant="button"
          component="div"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          Chapters
        </Typography>
        {showEditButton && (
          <Stack direction="row" spacing={1}>
            {isEditing && (
              <Button variant="text" color="inherit" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            )}
            <Button
              variant="text"
              color="primary"
              startIcon={isEditing ? <SaveOutlined /> : <EditOutlinedIcon />}
              onClick={handleEditOrSave}
              disabled={isSaving}
            >
              {isEditing ? 'Save Changes' : 'Edit'}
            </Button>
          </Stack>
        )}
      </Stack>
      <Stack mt="8px" spacing={1}>
        {chapters.length > 0
          ? editableChapters.map((chapter, index) => {
              // Convert "mm:ss" timecode to total seconds
              const timeInSeconds = parseTime(chapter.timecode);
              return (
                <Stack key={`${chapter.timecode}-${index}`} spacing={0.5} sx={{ width: '100%' }}>
                  <Stack direction="row" alignItems="baseline" spacing={0.5}>
                    {isEditing ? (
                      <Typography variant="body2" sx={{ minWidth: '42px', flexShrink: 0, fontSize: '14px' }}>
                        {formatTime(timeInSeconds)}
                      </Typography>
                    ) : (
                      <ChapterTitle
                        variant="body2"
                        onClick={() => handleChapterClick(timeInSeconds)}
                        sx={{ minWidth: '42px', flexShrink: 0, fontSize: '14px' }}
                      >
                        {formatTime(timeInSeconds)}
                      </ChapterTitle>
                    )}
                    {isEditing ? (
                      <Typography
                        variant="body2"
                        contentEditable
                        suppressContentEditableWarning
                        ref={(el: HTMLElement | null) => {
                          titleRefs.current[index] = el;
                        }}
                        sx={{
                          outline: 'none',
                          flex: 1,
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: 1.43,
                          letterSpacing: '0.4px',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '4px',
                          px: 0.5,
                        }}
                      >
                        {chapter.text}
                      </Typography>
                    ) : (
                      <ChapterTitle
                        variant="body1"
                        onClick={() => handleChapterClick(timeInSeconds)}
                        sx={{
                          display: 'block',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {chapter.text}
                      </ChapterTitle>
                    )}
                  </Stack>
                  {(chapter.summary || isEditing) &&
                    (isEditing ? (
                      <Typography
                        variant="body2"
                        contentEditable
                        suppressContentEditableWarning
                        ref={(el: HTMLElement | null) => {
                          summaryRefs.current[index] = el;
                        }}
                        sx={{
                          outline: 'none',
                          fontSize: '14px',
                          lineHeight: 1.43,
                          letterSpacing: '0.4px',
                          width: '100%',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '4px',
                          px: 0.5,
                          py: 0.25,
                          color: 'text.primary',
                        }}
                      >
                        {chapter.summary ?? ''}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        sx={{
                          fontSize: '14px',
                          lineHeight: 1.43,
                          letterSpacing: '0.4px',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {chapter.summary}
                      </Typography>
                    ))}
                </Stack>
              );
            })
          : renderEmptyChapters()}
      </Stack>
    </Box>
  );
};

export default DocumentChapters;
